import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_roleplay-store-10/artifacts/d28byfwz_ChatGPT%20Image%20Jul%2019%2C%202026%2C%2008_51_14%20AM.png";

const HERO_BG =
  "https://images.pexels.com/photos/35161204/pexels-photo-35161204.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920";

function Line({ text, delay = 0, className = "" }) {
  return (
    <span className="mask-line">
      <motion.span
        className={`mask-inner ${className}`}
        initial={{ y: "115%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        transition={{ duration: 1.05, delay, ease: [0.19, 1, 0.22, 1] }}
      >
        {text}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden flex items-end pb-16 md:pb-24"
    >
      {/* parallax bg */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -z-10"
      >
        <img
          src={HERO_BG}
          alt="London night street"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-[color:var(--bg-base)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_20%,rgba(230,26,26,0.15),transparent_55%)]" />
      </motion.div>

      {/* HUD dossier top */}
      <div className="absolute top-24 left-6 md:left-10 right-6 md:right-10 flex items-start justify-between z-content pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-400"
        >
          FILE / 001<br />
          SUBJECT: LONDON — TOWER BLOCK ESTATE<br />
          STATUS: ACTIVE
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="hidden md:block text-right font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-400"
        >
          51.5074° N<br />
          0.1278° W<br />
          THE ENDS
        </motion.div>
      </div>

      {/* parallax logo */}
      <motion.img
        src={LOGO_URL}
        alt="BrokenRP crest"
        style={{ y: logoY }}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 0.95, scale: 1 }}
        transition={{ duration: 1.6, delay: 0.4, ease: [0.19, 1, 0.22, 1] }}
        data-testid="hero-crest"
        className="absolute right-[-6vw] top-[8vh] w-[60vw] max-w-[720px] pointer-events-none drop-shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
      />

      {/* Kinetic type */}
      <motion.div
        style={{ opacity: textOpacity }}
        className="relative z-content max-w-[1440px] w-full mx-auto px-6 md:px-10"
      >
        <div className="mb-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="eyebrow"
          >
            / EVERY STORY STARTS BROKEN — SINCE 2026
          </motion.div>
        </div>

        <h1
          data-testid="hero-heading"
          className="font-display text-white tracking-tighter leading-[0.85] text-[16vw] md:text-[13vw] lg:text-[11vw] xl:text-[10rem] broken-text"
        >
          <Line text="EVERY" delay={0.15} />
          <Line text="STORY" delay={0.28} />
          <Line text="STARTS" delay={0.41} />
          <Line text="BROKEN." delay={0.54} className="text-[color:var(--accent-red)]" />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8 }}
          className="mt-8 md:mt-12 flex flex-col md:flex-row items-start md:items-end justify-between gap-8"
        >
          <p className="max-w-md text-zinc-300 text-base leading-relaxed">
            The official webstore for <span className="text-white">BrokenRP</span> — UK Roleplay.
            Starter packs, weapon loadouts, 1‑of‑1 metal, MLOs. Curated for the ends.
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/shop"
              data-testid="hero-cta-shop"
              className="btn-brutal group inline-flex items-center gap-3 h-12 pl-6 pr-4 border border-white text-white font-mono text-[11px] tracking-[0.24em] uppercase"
            >
              Enter The Store
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="https://discord.gg/brokenrp"
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-discord"
              className="hidden sm:inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-zinc-300 hover:text-white transition-colors"
            >
              Join Discord →
            </a>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500"
      >
        Scroll ↓
      </motion.div>
    </section>
  );
}
